import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const FREE_PLAN_LIMIT = 2;

interface PremiumContextType {
  isPremium: boolean;
  planCount: number;
  canGeneratePlan: boolean;
  remainingPlans: number;
  upgrade: () => void;
  incrementPlanCount: () => void;
  loading: boolean;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  planCount: 0,
  canGeneratePlan: true,
  remainingPlans: FREE_PLAN_LIMIT,
  upgrade: () => {},
  incrementPlanCount: () => {},
  loading: true,
});

export const PremiumProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [planCount, setPlanCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch premium status from database
  useEffect(() => {
    if (!user) {
      setIsPremium(false);
      setPlanCount(0);
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_premium, plan_generations_count")
        .eq("user_id", user.id)
        .single();

      if (!error && data) {
        setIsPremium(data.is_premium ?? false);
        setPlanCount(data.plan_generations_count ?? 0);
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const canGeneratePlan = isPremium || planCount < FREE_PLAN_LIMIT;
  const remainingPlans = Math.max(0, FREE_PLAN_LIMIT - planCount);

  const upgrade = useCallback(() => {
    // This will be replaced by Stripe payment flow
    // For now, just show a message
    toast.info("💳 Payment integration coming soon!");
  }, []);

  const incrementPlanCount = useCallback(() => {
    // Optimistic update — the server already incremented
    setPlanCount((prev) => prev + 1);
  }, []);

  return (
    <PremiumContext.Provider value={{ isPremium, planCount, canGeneratePlan, remainingPlans, upgrade, incrementPlanCount, loading }}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremium = () => useContext(PremiumContext);
export { FREE_PLAN_LIMIT };
